"use client"
import { type CSSProperties, useRef } from "react"
import Link from "next/link"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Asset } from "~/components/primitives/Asset/Asset"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { FullBleedCalloutInterface } from "./FullBleedCallout.interface"
import s from "./FullBleedCallout.module.scss"

const HeartIcon = () => (
  <svg
    className={s.heartIcon}
    viewBox="0 0 40 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M20 36C20 36 0 23.5 0 11C0 4.9 4.9 0 11 0C14.7 0 17.9 1.8 20 4.6C22.1 1.8 25.3 0 29 0C35.1 0 40 4.9 40 11C40 23.5 20 36 20 36Z"
      fill="currentColor"
    />
  </svg>
)

export type FullBleedCalloutViewProps = Omit<
  FullBleedCalloutInterface,
  "id"
> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const FullBleedCalloutView = ({
  moduleIndex,
  mediaType,
  backgroundImage,
  backgroundVideo,
  insetImage,
  headline,
  cta,
  config,
  className,
  style,
}: FullBleedCalloutViewProps) => {
  const configProps = getModuleConfigProps(config)
  const sectionRef = useRef<HTMLElement>(null)

  const isLinked = Boolean(cta?.url)
  const isExternal = cta?.url?.startsWith("http")

  const content = (
    <>
      <div className={s.backgroundLayer} aria-hidden="true">
        {!mediaType && backgroundImage?.desktop && (
          <ResponsiveAsset
            className={s.backgroundAsset}
            desktop={backgroundImage.desktop}
            mobile={backgroundImage.mobile ?? undefined}
            imageSizes={{ base: "100vw" }}
          />
        )}
        {mediaType && backgroundVideo && (
          <Asset
            className={s.backgroundAsset}
            asset={backgroundVideo}
            imageSizes={{ base: "100vw" }}
            videoProps={{
              autoPlay: true,
              muted: true,
              loop: true,
              playsInline: true,
            }}
          />
        )}
        <div className={s.blurOverlay} />
      </div>

      <div className={s.container}>
        <FadeIn element="div" className={s.insetImageWrapper}>
          {insetImage && (
            <Asset
              className={s.insetImage}
              asset={insetImage}
              imageSizes={{ base: "90vw", md: "35vw", lg: "25vw" }}
            />
          )}
          <HeartIcon />
        </FadeIn>

        {headline && (
          <div className={s.headline}>
            <FadeIn>
              <AnimateText element="h2" className={s.headlineText} delay={0.2}>
                <HtmlRenderer inline html={headline} />
              </AnimateText>
            </FadeIn>
          </div>
        )}
      </div>
    </>
  )

  if (isLinked && cta?.url) {
    return (
      <Link
        ref={sectionRef as React.Ref<HTMLAnchorElement>}
        href={cta.url}
        className={cn(
          s.fullBleedCallout,
          s["fullBleedCallout--linked"],
          `stack-module-${moduleIndex}`,
          className,
        )}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener" : undefined}
        aria-label={cta.label || "Learn more"}
        data-module="full-bleed-callout"
        {...configProps}
        style={{ ...configProps.style, ...style }}
      >
        {content}
      </Link>
    )
  }

  return (
    <section
      ref={sectionRef}
      className={cn(
        s.fullBleedCallout,
        `stack-module-${moduleIndex}`,
        className,
      )}
      data-module="full-bleed-callout"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className="container">{content}</div>
    </section>
  )
}
